import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';

// Lazy load pages for better performance
const EnterpriseLandingPage = React.lazy(() => import('../../pages/EnterpriseLandingPage'));

// Legal pages (public — no auth required)
const LegalHubPage                = React.lazy(() => import('../../pages/legal/LegalHubPage'));
const PrivacyPolicyPage           = React.lazy(() => import('../../pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage          = React.lazy(() => import('../../pages/legal/TermsOfServicePage'));
const CookiePolicyPage            = React.lazy(() => import('../../pages/legal/CookiePolicyPage'));
const DataProcessingAgreementPage = React.lazy(() => import('../../pages/legal/DataProcessingAgreementPage'));
const AcceptableUsePolicyPage     = React.lazy(() => import('../../pages/legal/AcceptableUsePolicyPage'));
const DataSubjectRequestsPage     = React.lazy(() => import('../../pages/legal/DataSubjectRequestsPage'));
const SharedKanbanPage            = React.lazy(() => import('../../pages/shared/SharedKanbanPage'));
const CertificateVerifyPage       = React.lazy(() => import('../../pages/shared/CertificateVerifyPage'));
const SharedSprintPage            = React.lazy(() => import('../../pages/shared/SharedSprintPage'));
const EnterpriseLoginPage = React.lazy(() => import('../../pages/auth/EnterpriseLoginPage'));
const RegisterPage = React.lazy(() => import('../../pages/auth/RegisterPage'));
const QRRegistrationPage = React.lazy(() => import('../../pages/auth/QRRegistrationPage'));
const ForgotPasswordPage = React.lazy(() => import('../../pages/auth/ForgotPasswordPage'));
const BottomHubBar = React.lazy(() => import('../layout/BottomHubBar'));
const SubOperatorsPage = React.lazy(() => import('../../pages/legal/SubOperatorsPage'));
const ResetPasswordPage = React.lazy(() => import('../../pages/auth/ResetPasswordPage'));

const FirstRunPage = React.lazy(() => import('../../pages/onboarding/FirstRunPage'));
const OnboardingFlow = React.lazy(() => import('../../pages/onboarding/OnboardingFlow'));

const DashboardPage = React.lazy(() => import('../../pages/dashboard/DashboardPage'));
const ManagersDashboard = React.lazy(() => import('../../pages/dashboard/ManagersDashboard'));
const TeamMoodAnalytics = React.lazy(() => import('../../pages/analytics/TeamMoodAnalytics'));
const TeamMoodPage = React.lazy(() => import('../../pages/wellness/TeamMoodPage'));
const PlatformMoodPage = React.lazy(() => import('../../pages/admin/PlatformMoodPage'));
const ProfilePage = React.lazy(() => import('../../pages/profile/ProfilePage'));
const SettingsPage = React.lazy(() => import('../../pages/settings/SettingsPage'));
const NotificationsPage = React.lazy(() => import('../../pages/notifications/NotificationsPage'));

// Gamification
const QuizPage = React.lazy(() => import('../../pages/gamification/QuizPage'));
const LeaderboardPage = React.lazy(() => import('../../pages/gamification/LeaderboardPage'));
const AchievementsPage = React.lazy(() => import('../../pages/gamification/AchievementsPage'));
const ChallengesPage = React.lazy(() => import('../../pages/gamification/ChallengesPage'));
const PointsHistoryPage = React.lazy(() => import('../../pages/gamification/PointsHistoryPage'));
const AchievementDetailPage = React.lazy(() => import('../../pages/gamification/AchievementDetailPage'));
const GameDetailPage = React.lazy(() => import('../../pages/gamification/GameDetailPage'));
const ChallengeDetailPage = React.lazy(() => import('../../pages/gamification/ChallengeDetailPage'));
const LeaderboardDetailPage = React.lazy(() => import('../../pages/gamification/LeaderboardDetailPage'));

const GamesHub = React.lazy(() => import('../../pages/games/GamesHub'));
const MemoryGame = React.lazy(() => import('../../pages/games/MemoryGame'));
const WordPuzzleGame = React.lazy(() => import('../../pages/games/WordPuzzleGame'));
const TriviaGame = React.lazy(() => import('../../pages/games/TriviaGame'));
const ReactionGame = React.lazy(() => import('../../pages/games/ReactionGame'));
const SpeedMathGame = React.lazy(() => import('../../pages/games/SpeedMathGame'));
const PatternGame = React.lazy(() => import('../../pages/games/PatternGame'));
const NarrativeQuestEngine = React.lazy(() => import('../../pages/games/NarrativeQuestEngine'));
const ScavengerExpeditionEngine = React.lazy(() => import('../../pages/games/ScavengerExpeditionEngine'));
const WorldBuildingEngine = React.lazy(() => import('../../pages/games/WorldBuildingEngine'));
const RolePlayingEngine = React.lazy(() => import('../../pages/games/RolePlayingEngine'));
const MicroChallengeEngine = React.lazy(() => import('../../pages/games/MicroChallengeEngine'));
const FitnessCoachGame = React.lazy(() => import('../../pages/games/FitnessCoachGame'));
const WellnessQuizGame = React.lazy(() => import('../../pages/games/WellnessQuizGame'));
const MealPlannerGame = React.lazy(() => import('../../pages/games/MealPlannerGame'));
const SnakeGame = React.lazy(() => import('../../pages/games/SnakeGame'));
const TileShuffleGame = React.lazy(() => import('../../pages/games/TileShuffleGame'));
const EbookQuizEditor = React.lazy(() => import('../../pages/admin/EbookQuizEditor'));
const PuzzleEditor = React.lazy(() => import('../../pages/admin/PuzzleEditor'));
const PuzzleGame = React.lazy(() => import('../../pages/games/PuzzleGame'));

// Learning
const LearningHub = React.lazy(() => import('../../pages/learning/LearningHub'));
const CoursePage = React.lazy(() => import('../../pages/learning/CoursePage'));
const LessonPlayer = React.lazy(() => import('../../pages/learning/LessonPlayer'));
const ArticlePage = React.lazy(() => import('../../pages/learning/ArticlePage'));
const VideoPage = React.lazy(() => import('../../pages/learning/VideoPage'));
const PodcastPage = React.lazy(() => import('../../pages/learning/PodcastPage'));
const MyLearningPage = React.lazy(() => import('../../pages/learning/MyLearningPage'));
const MicrolearningPage = React.lazy(() => import('../../pages/learning/MicrolearningPage'));
const CompanyPoliciesPage = React.lazy(() => import('../../pages/learning/CompanyPoliciesPage'));
const PolicyDetailPage = React.lazy(() => import('../../pages/policies/PolicyDetailPage'));
const CourseDetailPage = React.lazy(() => import('../../pages/learning/CourseDetailPage'));
const ModuleDetailPage = React.lazy(() => import('../../pages/learning/ModuleDetailPage'));
const QuizAttemptPage = React.lazy(() => import('../../pages/learning/QuizAttemptPage'));
const QuizResultsPage = React.lazy(() => import('../../pages/learning/QuizResultsPage'));
const LearningProgressPage = React.lazy(() => import('../../pages/learning/LearningProgressPage'));

const AnnouncementsPage = React.lazy(() => import('../../pages/announcements/AnnouncementsPage'));
const AnnouncementDetailPage = React.lazy(() => import('../../pages/announcements/AnnouncementDetailPage'));

// Chat
const ChatPage = React.lazy(() => import('../../pages/chat/ChatPage'));
const ThreadsPage = React.lazy(() => import('../../pages/chat/ThreadsPage'));

// Calendar & Documents
const CalendarPage = React.lazy(() => import('../../pages/calendar/CalendarPage'));
const DocumentsPage = React.lazy(() => import('../../pages/documents/DocumentsPage'));

// Streaming
const YouTubeLivePage = React.lazy(() => import('../../pages/streaming/YouTubeLivePage'));

// Pulse (Internal Social Media)
const PulsePage = React.lazy(() => import('../../pages/pulse/PulsePage'));

// Store (Points Redemption)
const StorePage = React.lazy(() => import('../../pages/store/StorePage'));

// Rewards
const RewardsDashboardPage = React.lazy(() => import('../../pages/rewards/RewardsDashboardPage'));

// Recognition
const RecognitionDashboardPage = React.lazy(() => import('../../pages/recognition/RecognitionDashboardPage'));
const RecognitionAdminPage = React.lazy(() => import('../../pages/admin/recognition/RecognitionAdminPage'));
const RecognitionSeasonsAdminPage = React.lazy(() => import('../../pages/admin/recognition/RecognitionSeasonsAdminPage'));

// Admin
const UserManagement = React.lazy(() => import('../../pages/admin/UserManagement'));
const QRRegistrationManagement = React.lazy(() => import('../../pages/admin/QRRegistrationManagement'));
const CompanyManagement = React.lazy(() => import('../../pages/admin/CompanyManagement'));
const ContentManagement = React.lazy(() => import('../../pages/admin/ContentManagement'));
const AnnouncementsManagement = React.lazy(() => import('../../pages/admin/ContentManagement').then(m => ({ default: m.AnnouncementsManagement })));
const PoliciesManagement = React.lazy(() => import('../../pages/admin/ContentManagement').then(m => ({ default: m.PoliciesManagement })));
const CourseManagement = React.lazy(() => import('../../pages/admin/CourseManagement'));
const AnalyticsPage = React.lazy(() => import('../../pages/admin/AnalyticsPage'));
const SSOConfigPage = React.lazy(() => import('../../pages/admin/SSOConfigPage'));
const SystemJobsPage = React.lazy(() => import('../../pages/admin/SystemJobsPage'));
const GameManagement = React.lazy(() => import('../../pages/admin/games/GameManagement'));
const RewardsManagement = React.lazy(() => import('../../pages/admin/rewards/RewardsManagement'));
const GameCreator = React.lazy(() => import('../../pages/admin/GameCreator'));
const QuestionnaireManagement = React.lazy(() => import('../../pages/admin/QuestionnaireManagement'));
const DailyWellnessQuestionsManagement = React.lazy(() => import('../../pages/admin/wellness/DailyWellnessQuestionsManagement'));
const WellnessInterventionsPage = React.lazy(() => import('../../pages/admin/wellness/WellnessInterventionsPage'));
const WellnessChallengesAdmin = React.lazy(() => import('../../pages/admin/WellnessChallengesAdmin'));
const StreakAwardsAdmin = React.lazy(() => import('../../pages/admin/StreakAwardsAdmin'));
const LearningPathsAdminPage = React.lazy(() => import('../../pages/admin/lms/LearningPathsAdminPage'));
const FacilitatorAdminPage = React.lazy(() => import('../../pages/admin/lms/FacilitatorAdminPage'));
const JourneysListPage = React.lazy(() => import('../../pages/admin/lms/JourneysListPage'));
const MoreMeCoursesPage = React.lazy(() => import('../../pages/admin/lms/MoreMeCoursesPage'));
const PathBuilderPage = React.lazy(() => import('../../pages/admin/lms/PathBuilderPage'));
const LearningTrailPage = React.lazy(() => import('../../pages/learning/LearningTrailPage'));
const JourneyBuilderPage = React.lazy(() => import('../../pages/admin/lms/JourneyBuilderPage'));
const JourneyWizardPage = React.lazy(() => import('../../pages/admin/lms/JourneyWizardPage'));
const JourneyMonitorPage = React.lazy(() => import('../../pages/admin/lms/JourneyMonitorPage'));
const AssessmentSubmissionsPage = React.lazy(() => import('../../pages/admin/lms/AssessmentSubmissionsPage'));
const ReportCenterPage = React.lazy(() => import('../../pages/admin/lms/ReportCenterPage'));
const MyCertificatesPage = React.lazy(() => import('../../pages/learning/MyCertificatesPage'));
const CertificateViewPage = React.lazy(() => import('../../pages/learning/CertificateViewPage'));
const ManagerNominatePage = React.lazy(() => import('../../pages/learning/ManagerNominatePage'));
const ApprovalsPage = React.lazy(() => import('../../pages/learning/ApprovalsPage'));
const MyLearningJourneysPage = React.lazy(() => import('../../pages/learning/MyLearningJourneysPage'));
const LearningJourneyPlayerPage = React.lazy(() => import('../../pages/learning/LearningJourneyPlayerPage'));
const JourneyOverviewPage = React.lazy(() => import('../../pages/learning/JourneyOverviewPage'));
const PlacementAssessmentPage = React.lazy(() => import('../../pages/learning/PlacementAssessmentPage'));
const AIBotConfigPage = React.lazy(() => import('../../pages/admin/lms/AIBotConfigPage'));
const FeatureFlagsPage = React.lazy(() => import('../../pages/admin/FeatureFlagsPage'));
const CourseEditor = React.lazy(() => import('../../pages/admin/CourseEditor'));
const LessonEditor = React.lazy(() => import('../../pages/admin/LessonEditor'));
const ModuleEditor = React.lazy(() => import('../../pages/admin/ModuleEditor'));
const AdminLMSDashboard = React.lazy(() => import('../../pages/admin/AdminLMSDashboard'));
const QuizBuilder = React.lazy(() => import('../../pages/admin/QuizBuilder'));
const ManagerLearningDashboard = React.lazy(() => import('../../pages/dashboard/ManagerLearningDashboard'));
const PolicyEditor = React.lazy(() => import('../../pages/admin/PolicyEditor'));
const AnnouncementEditor = React.lazy(() => import('../../pages/admin/AnnouncementEditor'));
const UserDetailPage = React.lazy(() => import('../../pages/admin/UserDetailPage'));
const UserEditor = React.lazy(() => import('../../pages/admin/UserEditor'));
const QuestionnaireDetailPage = React.lazy(() => import('../../pages/admin/QuestionnaireDetailPage'));
const QuestionnaireEditor = React.lazy(() => import('../../pages/admin/QuestionnaireEditor'));
const GameEditor = React.lazy(() => import('../../pages/admin/GameEditor'));
const GameLevelEditor = React.lazy(() => import('../../pages/admin/GameLevelEditor'));
const LMSIntegrationPage = React.lazy(() => import('../../pages/admin/LMSIntegrationPage'));
const ReportDetailPage = React.lazy(() => import('../../pages/admin/ReportDetailPage'));
const DepartmentDetailPage = React.lazy(() => import('../../pages/admin/DepartmentDetailPage'));
const DepartmentEditor = React.lazy(() => import('../../pages/admin/DepartmentEditor'));
const ContentModerationPage = React.lazy(() => import('../../pages/admin/ContentModerationPage'));
const PolicyAcknowledgmentReport = React.lazy(() => import('../../pages/admin/PolicyAcknowledgmentReport'));
const AuditLogPage = React.lazy(() => import('../../pages/admin/AuditLogPage'));
const AiActivityPage = React.lazy(() => import('../../pages/admin/AiActivityPage'));
const SystemSettingsPage = React.lazy(() => import('../../pages/admin/SystemSettingsPage'));
const NotificationManagementPage = React.lazy(() => import('../../pages/admin/NotificationManagementPage'));
const BackupRestorePage = React.lazy(() => import('../../pages/admin/BackupRestorePage'));
const IntegrationSettingsPage = React.lazy(() => import('../../pages/admin/IntegrationSettingsPage'));
const RolePermissionsPage = React.lazy(() => import('../../pages/admin/RolePermissionsPage'));
const ModuleSettingsPage = React.lazy(() => import('../../pages/admin/ModuleSettingsPage'));
const GameEditorAdvanced = React.lazy(() => import('../../pages/admin/GameEditorAdvanced'));
const SuperAdminDashboard = React.lazy(() => import('../../pages/admin/SuperAdminDashboard'));
const SystemConfigPage = React.lazy(() => import('../../pages/admin/SystemConfigPage'));
const UserImportPage = React.lazy(() => import('../../pages/admin/UserImportPage'));
const RewardPointsConfigPage = React.lazy(() => import('../../pages/admin/RewardPointsConfigPage'));
const StoreAdminPage = React.lazy(() => import('../../pages/admin/StoreAdminPage'));
const AdminSettingsPage = React.lazy(() => import('../../pages/admin/AdminSettingsPage'));
const AdminHub = React.lazy(() => import('../../pages/admin/AdminHub'));
const OrgHierarchyPage = React.lazy(() => import('../../pages/admin/OrgHierarchyPage'));
const LMSManagement = React.lazy(() => import('../../pages/admin/LMSManagement'));
const CompanyBrandingPage = React.lazy(() => import('../../pages/admin/CompanyBrandingPage'));
const AdminAchievementsPage = React.lazy(() => import('../../pages/admin/AdminAchievementsPage'));
const NewAchievementsPage = React.lazy(() => import('../../pages/achievements/AchievementsPage'));

// Tasks
const KanbanBoard = React.lazy(() => import('../../pages/tasks/KanbanBoard'));
const MyTasksPage = React.lazy(() => import('../../pages/tasks/MyTasksPage'));
const TaskManagerHub = React.lazy(() => import('../../pages/tasks/TaskManagerHub'));
const WorkstreamPage = React.lazy(() => import('../../pages/tasks/templates/WorkstreamPage'));
const KanbanTaskDetailPage = React.lazy(() => import('../../pages/tasks/TaskDetailPage'));
const TasksCalendar = React.lazy(() => import('../../pages/tasks/TasksCalendar'));
const PublicBoardPage = React.lazy(() => import('../../pages/tasks/PublicBoardPage'));
const SupportPortalPage = React.lazy(() => import('../../pages/support/SupportPortalPage'));
const SupportTicketDetailPage = React.lazy(() => import('../../pages/support/SupportTicketDetailPage'));
const SupportAdminPage = React.lazy(() => import('../../pages/admin/support/SupportAdminPage'));
const SupportKbAdminPage = React.lazy(() => import('../../pages/admin/support/SupportKbAdminPage'));
const SystemErrorsPage = React.lazy(() => import('../../pages/admin/SystemErrorsPage'));

const ReportsPage = React.lazy(() => import('../../pages/reports/ReportsPage'));

// Projects & Tasks

const TeamMembersPage = React.lazy(() => import('../../pages/team/TeamMembersPage'));
const TeamMemberDetailPage = React.lazy(() => import('../../pages/team/TeamMemberDetailPage'));
const TeamSentimentPage = React.lazy(() => import('../../pages/team/TeamSentimentPage'));

const WellnessPage = React.lazy(() => import('../../pages/wellness/WellnessPage'));
const WellnessHistoryPage = React.lazy(() => import('../../pages/wellness/WellnessHistoryPage'));
const WellnessDetailPage = React.lazy(() => import('../../pages/wellness/WellnessDetailPage'));
const WellnessAnalyticsPage = React.lazy(() => import('../../pages/wellness/WellnessAnalyticsPage'));
const StreaksPage = React.lazy(() => import('../../pages/wellness/StreaksPage'));
const WellnessChallengesPage = React.lazy(() => import('../../pages/wellness/ChallengesPage'));

const LibraryPage = React.lazy(() => import('../../pages/library/LibraryPage'));
const BookReaderPage = React.lazy(() => import('../../pages/library/BookReaderPage'));
const LibraryAdminPage = React.lazy(() => import('../../pages/library/LibraryAdminPage'));

const MyLeavePage = React.lazy(() => import('../../pages/leave/MyLeavePage'));
const LeaveApprovalsPage = React.lazy(() => import('../../pages/leave/LeaveApprovalsPage'));
const LeaveAdminPage = React.lazy(() => import('../../pages/leave/LeaveAdminPage'));
const ManagerLeaveReportPage = React.lazy(() => import('../../pages/leave/ManagerLeaveReportPage'));
const InsightsPage = React.lazy(() => import('../../pages/insights/InsightsPage'));

const AnnouncementInsightsPage = React.lazy(() => import('../../pages/admin/AnnouncementInsightsPage'));
const CategoriesAdminPage = React.lazy(() => import('../../pages/admin/CategoriesAdminPage'));
const MicrolearningContentAdminPage = React.lazy(() => import('../../pages/admin/MicrolearningContentAdminPage'));
const MicrolearningUsersAdminPage = React.lazy(() => import('../../pages/admin/MicrolearningUsersAdminPage'));
const MyPerformancePage = React.lazy(() => import('../../pages/performance/MyPerformancePage'));
const ManagerPerformancePage = React.lazy(() => import('../../pages/performance/ManagerPerformancePage'));
const PerformanceAdminPage = React.lazy(() => import('../../pages/performance/PerformanceAdminPage'));
const PerformanceReportsPage = React.lazy(() => import('../../pages/performance/PerformanceReportsPage'));
const LiveQuizPlayPage = React.lazy(() => import('../../pages/livequiz/LiveQuizPlayPage'));
const LiveQuizHostPage = React.lazy(() => import('../../pages/livequiz/LiveQuizHostPage'));
const SurveysListPage = React.lazy(() => import('../../pages/surveys/SurveysListPage'));
const SurveyTakePage = React.lazy(() => import('../../pages/surveys/SurveyTakePage'));
const SurveyAdminListPage = React.lazy(() => import('../../pages/surveys/SurveyAdminListPage'));
const SurveyBuilderPage = React.lazy(() => import('../../pages/surveys/SurveyBuilderPage'));
const SurveyReportPage = React.lazy(() => import('../../pages/surveys/SurveyReportPage'));

const ProfileEditor = React.lazy(() => import('../../pages/profile/ProfileEditor'));
const NotificationSettingsPage = React.lazy(() => import('../../pages/profile/NotificationSettingsPage'));
const SecuritySettingsPage = React.lazy(() => import('../../pages/profile/SecuritySettingsPage'));
const PreferencesPage = React.lazy(() => import('../../pages/profile/PreferencesPage'));

const DiscussionThreadPage = React.lazy(() => import('../../pages/social/DiscussionThreadPage'));
const CreateAnnouncementPage = React.lazy(() => import('../../pages/social/CreateAnnouncementPage'));

// Culture Hub
const CultureHubPage = React.lazy(() => import('../../pages/culture/CultureHubPage'));
const CultureAnalyticsPage = React.lazy(() => import('../../pages/culture/CultureAnalyticsPage'));
const CultureTeamReportPage = React.lazy(() => import('../../pages/culture/CultureTeamReportPage'));

// AI
const CompanyAIChatPage = React.lazy(() => import('../../pages/ai/CompanyAIChatPage'));

// Error pages
const NotFoundPage = React.lazy(() => import('../../pages/error/NotFoundPage'));
const UnauthorizedPage = React.lazy(() => import('../../pages/error/UnauthorizedPage'));

// /tasks/<id> -> the task manager with that task opened (it reads #task-<id>).
function TaskIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={/^[0-9]+$/.test(id || '') ? `/tasks/manager#task-${id}` : '/tasks/manager'} replace />;
}

// Old Snake editor links -> the game opened for editing in Game Management.
function SnakeEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={/^[0-9]+$/.test(id || '') ? `/admin/games?type=snake&edit=${id}` : '/admin/games'} replace />;
}

function AppRoutes() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Legal pages — always public, no auth wrapper */}
        <Route path="/legal"                element={<LegalHubPage />} />
        <Route path="/legal/privacy"        element={<PrivacyPolicyPage />} />
        <Route path="/legal/terms"          element={<TermsOfServicePage />} />
        <Route path="/legal/cookies"        element={<CookiePolicyPage />} />
        <Route path="/legal/dpa"            element={<DataProcessingAgreementPage />} />
        <Route path="/legal/sub-operators"  element={<SubOperatorsPage />} />
        <Route path="/legal/aup"            element={<AcceptableUsePolicyPage />} />
        <Route path="/legal/data-requests"  element={<DataSubjectRequestsPage />} />

        {/* 3rd-party share links — no auth, token in URL is the credential */}
        <Route path="/shared/workstream/:token" element={<SharedKanbanPage />} />
        <Route path="/verify-certificate/:code" element={<CertificateVerifyPage />} />
        <Route path="/shared/sprint/:token"     element={<SharedSprintPage />} />

        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <EnterpriseLoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />
        <Route path="/register/qr/:token" element={
          <PublicRoute>
            <QRRegistrationPage />
          </PublicRoute>
        } />
        <Route path="/forgot-password" element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } />
        <Route path="/reset-password" element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        } />

        {/* Onboarding Routes */}
        {/* First run. The route keeps its old path so every existing redirect,
            gate and test still points at the right screen. */}
        <Route path="/questionnaire" element={
          <ProtectedRoute>
            <FirstRunPage />
          </ProtectedRoute>
        } />

        {/* Public Landing Page */}
        <Route path="/" element={
          <PublicRoute>
            <EnterpriseLandingPage />
          </PublicRoute>
        } />

        {/* Protected Routes */}

        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } />

        <Route path="/dashboard/team-mood" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin']}>
            <ManagersDashboard />
          </ProtectedRoute>
        } />

        <Route path="/wellness/team-mood" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <TeamMoodPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/mood-analytics" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <PlatformMoodPage />
          </ProtectedRoute>
        } />

        <Route path="/analytics/team-mood" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin']}>
            <TeamMoodAnalytics />
          </ProtectedRoute>
        } />

        <Route path="/notifications" element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } />
        <Route path="/app/notifications" element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/profile/edit" element={
          <ProtectedRoute>
            <ProfileEditor />
          </ProtectedRoute>
        } />
        <Route path="/profile/notifications" element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/profile/security" element={
          <ProtectedRoute>
            <SecuritySettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/profile/preferences" element={
          <ProtectedRoute>
            <PreferencesPage />
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />

        {/* Gamification Routes */}
        <Route path="/gamification" element={
          <ProtectedRoute>
            <GamesHub />
          </ProtectedRoute>
        } />
        <Route path="/gamification/quiz/:id" element={
          <ProtectedRoute>
            <QuizPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/leaderboard" element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/achievements" element={
          <ProtectedRoute>
            <AchievementsPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/challenges" element={
          <ProtectedRoute>
            <ChallengesPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/points" element={
          <ProtectedRoute>
            <PointsHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/points-history" element={
          <ProtectedRoute>
            <PointsHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/achievements/:id" element={
          <ProtectedRoute>
            <AchievementDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/games/:id" element={
          <ProtectedRoute>
            <GameDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/challenges/:id" element={
          <ProtectedRoute>
            <ChallengeDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/gamification/leaderboard/:type" element={
          <ProtectedRoute>
            <LeaderboardDetailPage />
          </ProtectedRoute>
        } />

        {/* Games Routes */}
        <Route path="/games" element={
          <ProtectedRoute>
            <GamesHub />
          </ProtectedRoute>
        } />
        <Route path="/games/memory" element={
          <ProtectedRoute>
            <MemoryGame />
          </ProtectedRoute>
        } />
        <Route path="/games/snake" element={
          <ProtectedRoute>
            <SnakeGame />
          </ProtectedRoute>
        } />
        <Route path="/games/snake/:id" element={
          <ProtectedRoute>
            <SnakeGame />
          </ProtectedRoute>
        } />
        <Route path="/games/tile-shuffle" element={
          <ProtectedRoute>
            <TileShuffleGame />
          </ProtectedRoute>
        } />
        <Route path="/games/tile-shuffle/:id" element={
          <ProtectedRoute>
            <TileShuffleGame />
          </ProtectedRoute>
        } />
        <Route path="/games/word-puzzle" element={
          <ProtectedRoute>
            <WordPuzzleGame />
          </ProtectedRoute>
        } />
        <Route path="/games/trivia" element={
          <ProtectedRoute>
            <TriviaGame />
          </ProtectedRoute>
        } />
        <Route path="/games/reaction" element={
          <ProtectedRoute>
            <ReactionGame />
          </ProtectedRoute>
        } />
        <Route path="/games/speed-math" element={
          <ProtectedRoute>
            <SpeedMathGame />
          </ProtectedRoute>
        } />
        <Route path="/games/pattern" element={
          <ProtectedRoute>
            <PatternGame />
          </ProtectedRoute>
        } />
        <Route path="/games/narrative-quest/:id" element={
          <ProtectedRoute>
            <NarrativeQuestEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/scavenger-expedition/:id" element={
          <ProtectedRoute>
            <ScavengerExpeditionEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/world-building/:id" element={
          <ProtectedRoute>
            <WorldBuildingEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/role-playing/:id" element={
          <ProtectedRoute>
            <RolePlayingEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/micro-challenge/:id" element={
          <ProtectedRoute>
            <MicroChallengeEngine />
          </ProtectedRoute>
        } />
        <Route path="/games/fitness-coach" element={
          <ProtectedRoute>
            <FitnessCoachGame />
          </ProtectedRoute>
        } />
        <Route path="/games/wellness-quiz" element={
          <ProtectedRoute>
            <WellnessQuizGame />
          </ProtectedRoute>
        } />
        <Route path="/games/meal-planner" element={
          <ProtectedRoute>
            <MealPlannerGame />
          </ProtectedRoute>
        } />

        {/* Learning Routes */}
        <Route path="/learning" element={
          <ProtectedRoute>
            <LearningHub />
          </ProtectedRoute>
        } />
        <Route path="/learning/my-learning" element={
          <ProtectedRoute>
            <MyLearningPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/course/:id" element={
          <ProtectedRoute>
            <CoursePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/course-detail/:id" element={
          <ProtectedRoute>
            <CourseDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/module/:id" element={
          <ProtectedRoute>
            <ModuleDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/quiz/:id" element={
          <ProtectedRoute>
            <QuizAttemptPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/quiz/:id/results" element={
          <ProtectedRoute>
            <QuizResultsPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/progress" element={
          <ProtectedRoute>
            <LearningProgressPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/course/:courseId/lesson/:lessonId" element={
          <ProtectedRoute>
            <LessonPlayer />
          </ProtectedRoute>
        } />
        <Route path="/learning/articles" element={
          <ProtectedRoute>
            <ArticlePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/article/:id" element={
          <ProtectedRoute>
            <ArticlePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/videos" element={
          <ProtectedRoute>
            <VideoPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/video/:id" element={
          <ProtectedRoute>
            <VideoPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/podcasts" element={
          <ProtectedRoute>
            <PodcastPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/podcast/:id" element={
          <ProtectedRoute>
            <PodcastPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/microlearning" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/path/:id" element={
          <ProtectedRoute>
            <LearningProgressPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/journey/:id" element={
          <ProtectedRoute>
            <LearningProgressPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/microlearning/:id" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/microlearning" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/microlearning/:id" element={
          <ProtectedRoute>
            <MicrolearningPage />
          </ProtectedRoute>
        } />
        <Route path="/policies" element={
          <ProtectedRoute>
            <CompanyPoliciesPage />
          </ProtectedRoute>
        } />
        <Route path="/policies/:id" element={
          <ProtectedRoute>
            <PolicyDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/announcements" element={
          <ProtectedRoute>
            <AnnouncementsPage />
          </ProtectedRoute>
        } />
        <Route path="/announcements/create" element={
          <ProtectedRoute>
            <CreateAnnouncementPage />
          </ProtectedRoute>
        } />
        <Route path="/announcements/:id" element={
          <ProtectedRoute>
            <AnnouncementDetailPage />
          </ProtectedRoute>
        } />

        {/* Chat Routes */}
        <Route path="/chat" element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="/chat/:conversationId" element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } />
                <Route path="/threads" element={
                  <ProtectedRoute>
                    <ThreadsPage />
                  </ProtectedRoute>
                } />

                {/* Calendar Routes */}
                <Route path="/calendar" element={
                  <ProtectedRoute>
                    <CalendarPage />
                  </ProtectedRoute>
                } />

                                {/* Documents Routes */}
                                <Route path="/documents" element={
                                  <ProtectedRoute>
                                    <DocumentsPage />
                                  </ProtectedRoute>
                                } />

                                {/* Streaming Routes */}
                                <Route path="/streaming" element={
                                  <ProtectedRoute>
                                    <YouTubeLivePage />
                                  </ProtectedRoute>
                                } />
                                <Route path="/live" element={
                                  <ProtectedRoute>
                                    <YouTubeLivePage />
                                  </ProtectedRoute>
                                } />

        {/* Culture Hub Routes */}
        <Route path="/culture" element={
          <ProtectedRoute>
            <CultureHubPage />
          </ProtectedRoute>
        } />
        <Route path="/culture/analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CultureAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/culture/team-report" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin']}>
            <CultureTeamReportPage />
          </ProtectedRoute>
        } />

        <Route path="/marketplace" element={<Navigate to="/store" replace />} />
        <Route path="/marketplace/*" element={<Navigate to="/store" replace />} />

                {/* Store (Points Redemption) */}
                <Route path="/store" element={
                  <ProtectedRoute>
                    <StorePage />
                  </ProtectedRoute>
                } />

                {/* Rewards Routes */}
                <Route path="/rewards" element={
                  <ProtectedRoute>
                    <RewardsDashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/rewards/dashboard" element={
                  <ProtectedRoute>
                    <RewardsDashboardPage />
                  </ProtectedRoute>
                } />
                {/* The live store is /store; the old marketplace page called an endpoint that does not exist. */}
                <Route path="/rewards/store" element={<Navigate to="/store" replace />} />
                <Route path="/rewards/marketplace" element={<Navigate to="/store" replace />} />

                {/* Achievements Routes */}
                <Route path="/achievements" element={
                  <ProtectedRoute>
                    <NewAchievementsPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/achievements" element={
                  <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
                    <AdminAchievementsPage />
                  </ProtectedRoute>
                } />

                {/* Projects & Tasks Routes */}
                {/* The Projects module is replaced by Tasks: old links land on the task manager. */}
                <Route path="/projects" element={<Navigate to="/tasks/manager" replace />} />
                <Route path="/projects/*" element={<Navigate to="/tasks/manager" replace />} />

        {/* Team Routes */}
        <Route path="/team/members" element={
          <ProtectedRoute>
            <TeamMembersPage />
          </ProtectedRoute>
        } />
        <Route path="/team/members/:id" element={
          <ProtectedRoute>
            <TeamMemberDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/team/sentiment" element={
          <ProtectedRoute>
            <TeamSentimentPage />
          </ProtectedRoute>
        } />
        {/* There is no /team/analytics backend of its own; team mood is the live view. */}
        <Route path="/team/analytics" element={<Navigate to="/analytics/team-mood" replace />} />
        
                <Route path="/wellness" element={
                  <ProtectedRoute>
                    <WellnessPage />
                  </ProtectedRoute>
                } />
                <Route path="/wellness/history" element={
                  <ProtectedRoute>
                    <WellnessHistoryPage />
                  </ProtectedRoute>
                } />
        <Route path="/wellness/check-in/:id" element={
          <ProtectedRoute>
            <WellnessDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/wellness/analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <WellnessAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/wellness/streaks" element={
          <ProtectedRoute>
            <StreaksPage />
          </ProtectedRoute>
        } />
        <Route path="/wellness/challenges" element={
          <ProtectedRoute>
            <WellnessChallengesPage />
          </ProtectedRoute>
        } />

        {/* Library — now lives inside LMS as a Learning Hub tab.
            Legacy /library paths are kept as aliases so old links
            don't 404; the canonical entry point is /learning?tab=library. */}
        <Route path="/library" element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        } />
        <Route path="/library/manage" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LibraryAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/library/:id" element={
          <ProtectedRoute>
            <BookReaderPage />
          </ProtectedRoute>
        } />
        {/* New canonical LMS-namespaced routes */}
        <Route path="/learning/library/:id" element={
          <ProtectedRoute>
            <BookReaderPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/library" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LibraryAdminPage />
          </ProtectedRoute>
        } />

        {/* Leave Management */}
        <Route path="/leave" element={
          <ProtectedRoute>
            <MyLeavePage />
          </ProtectedRoute>
        } />
        {/* Any signed-in user: a line manager can have the plain employee role; the API scopes the queue to the people they manage. */}
        <Route path="/manager/leave" element={
          <ProtectedRoute>
            <LeaveApprovalsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/leave" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LeaveAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/manager/leave-report" element={
          <ProtectedRoute>
            <ManagerLeaveReportPage />
          </ProtectedRoute>
        } />

        {/* Insights — company-wide BI & analytics (admin) */}
        <Route path="/insights" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <InsightsPage />
          </ProtectedRoute>
        } />

                <Route path="/social/thread/:id" element={
                  <ProtectedRoute>
                    <DiscussionThreadPage />
                  </ProtectedRoute>
                } />

                {/* Pulse (Internal Social Media) */}
                <Route path="/pulse" element={
                  <ProtectedRoute>
                    <PulsePage />
                  </ProtectedRoute>
                } />

                {/* Alias Routes for convenience */}
        <Route path="/leaderboard" element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />

        {/* Recognition Routes */}
        <Route path="/recognition" element={
          <ProtectedRoute>
            <RecognitionDashboardPage />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AdminHub />
          </ProtectedRoute>
        } />
        <Route path="/admin/categories" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CategoriesAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/microlearning/content" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <MicrolearningContentAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/microlearning/users" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <MicrolearningUsersAdminPage />
          </ProtectedRoute>
        } />
        {/* Performance management */}
        <Route path="/performance" element={
          <ProtectedRoute>
            <MyPerformancePage />
          </ProtectedRoute>
        } />
        <Route path="/manager/performance" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <ManagerPerformancePage />
          </ProtectedRoute>
        } />
        <Route path="/admin/performance" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PerformanceAdminPage />
          </ProtectedRoute>
        } />
        {/* Reporting: admins see the company, managers/leads their own team (backend-scoped) */}
        <Route path="/performance/reports" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <PerformanceReportsPage />
          </ProtectedRoute>
        } />
        {/* Live quiz (town hall) */}
        <Route path="/live-quiz" element={
          <ProtectedRoute>
            <LiveQuizPlayPage />
          </ProtectedRoute>
        } />
        <Route path="/live-quiz/host" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <LiveQuizHostPage />
          </ProtectedRoute>
        } />
        {/* Questionnaires / Surveys */}
        <Route path="/surveys" element={
          <ProtectedRoute>
            <SurveysListPage />
          </ProtectedRoute>
        } />
        <Route path="/surveys/:id" element={
          <ProtectedRoute>
            <SurveyTakePage />
          </ProtectedRoute>
        } />
        <Route path="/admin/surveys" element={
          <ProtectedRoute requiredRoles={['manager', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <SurveyAdminListPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/surveys/:id" element={
          <ProtectedRoute requiredRoles={['manager', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <SurveyBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/surveys/:id/report" element={
          <ProtectedRoute requiredRoles={['manager', 'hr', 'admin', 'company-super-admin', 'super-super-admin']}>
            <SurveyReportPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/qr-registration" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QRRegistrationManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/companies" element={
          <ProtectedRoute requiredRoles={['company-super-admin', 'super-super-admin']}>
            <CompanyManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/content" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ContentManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PoliciesManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CourseManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics"element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/modules" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ModuleSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/sso" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SSOConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/system-jobs" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SystemJobsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/games" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/rewards" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RewardsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/recognition" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RecognitionAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/recognition/seasons" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RecognitionSeasonsAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/game-creator" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameCreator />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/daily-wellness-questions" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DailyWellnessQuestionsManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/wellness/interventions" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin', 'manager']}>
            <WellnessInterventionsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/wellness/challenges" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <WellnessChallengesAdmin />
          </ProtectedRoute>
        } />
        <Route path="/admin/wellness/streak-awards" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <StreakAwardsAdmin />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/paths" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LearningPathsAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/facilitators" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <FacilitatorAdminPage />
          </ProtectedRoute>
        } />
        {/* No requiredRoles — SDF is a privilege independent of base role,
            so any authenticated user can hit these; the pages themselves
            check /lms-journeys/me/status and show a "not authorized" panel. */}
        <Route path="/admin/lms/journeys" element={
          <ProtectedRoute>
            <JourneysListPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/journeys/:id/build" element={
          <ProtectedRoute>
            <JourneyBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/journeys/:id/build/wizard" element={
          <ProtectedRoute>
            <JourneyWizardPage />
          </ProtectedRoute>
        } />
        {/* Preview as learner: the real player, in preview mode, on a course
            the admin owns — no enrolment, nothing recorded. */}
        <Route path="/admin/lms/journeys/:id/preview/:moduleId" element={
          <ProtectedRoute>
            <LearningJourneyPlayerPage preview />
          </ProtectedRoute>
        } />
        {/* Courses are the same engine and the same pages, addressed under
            /admin/lms/courses so the builder can hide cohorts, scored
            assessments and adaptive test-out and say "course" throughout. */}
        {/* Learning paths on the journeys engine: a trail of journeys AND
            courses. The legacy /admin/lms/paths page and /learning/path/:id
            stay for tenants with old-engine data. */}
        <Route path="/admin/lms/learning-paths" element={
          <ProtectedRoute>
            <PathBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/learning-paths/:id" element={
          <ProtectedRoute>
            <PathBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/paths" element={
          <ProtectedRoute>
            <LearningTrailPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/paths/:id" element={
          <ProtectedRoute>
            <LearningTrailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/moreme-courses" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <MoreMeCoursesPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/courses" element={
          <ProtectedRoute>
            <JourneysListPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/courses/:id/build" element={
          <ProtectedRoute>
            <JourneyBuilderPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/courses/:id/build/wizard" element={
          <ProtectedRoute>
            <JourneyWizardPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/courses/:id/preview/:moduleId" element={
          <ProtectedRoute>
            <LearningJourneyPlayerPage preview />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/journeys/:id/monitor" element={
          <ProtectedRoute>
            <JourneyMonitorPage />
          </ProtectedRoute>
        } />
        {/* Uploaded assessment answers: what learners handed in, marked here. */}
        <Route path="/admin/lms/journeys/:id/submissions" element={
          <ProtectedRoute>
            <AssessmentSubmissionsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/reports" element={
          <ProtectedRoute>
            <ReportCenterPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/certificates" element={
          <ProtectedRoute>
            <MyCertificatesPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/certificates/:code" element={
          <ProtectedRoute>
            <CertificateViewPage />
          </ProtectedRoute>
        } />
        <Route path="/manager/nominate-learning" element={
          <ProtectedRoute>
            <ManagerNominatePage />
          </ProtectedRoute>
        } />
        <Route path="/learning/approvals" element={
          <ProtectedRoute>
            <ApprovalsPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/journeys" element={
          <ProtectedRoute>
            <MyLearningJourneysPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/journeys/module/:moduleId" element={
          <ProtectedRoute>
            <LearningJourneyPlayerPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/journeys/:courseId/placement" element={
          <ProtectedRoute>
            <PlacementAssessmentPage />
          </ProtectedRoute>
        } />
        <Route path="/learning/journeys/:courseId" element={
          <ProtectedRoute>
            <JourneyOverviewPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms/ai-bot" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AIBotConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/feature-flags" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <FeatureFlagsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CourseEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CourseEditor />
          </ProtectedRoute>
        } />
        {/* ── New LMS Enhanced Routes ── */}
        <Route path="/admin/lms" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AdminLMSDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses/:courseId/quiz/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuizBuilder />
          </ProtectedRoute>
        } />
        <Route path="/admin/courses/:courseId/quiz/:quizId/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuizBuilder />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/team-learning" element={
          <ProtectedRoute requiredRoles={['manager', 'lead', 'admin', 'company-super-admin', 'super-super-admin']}>
            <ManagerLearningDashboard />
          </ProtectedRoute>
        } />

        <Route path="/admin/modules/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ModuleEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/modules/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ModuleEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/lessons/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LessonEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/lessons/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LessonEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PolicyEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PolicyEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/:id/acknowledgments" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PolicyAcknowledgmentReport />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/questionnaires/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <QuestionnaireEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditor />
          </ProtectedRoute>
        } />
        {/* Snake is made in Game Management now; old editor links open it there. */}
        <Route path="/admin/games/snake/new" element={<Navigate to="/admin/games?create=snake" replace />} />
        <Route path="/admin/games/snake/:id/edit" element={<SnakeEditRedirect />} />
        <Route path="/admin/games/ebook-quiz/new" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <EbookQuizEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/ebook-quiz/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <EbookQuizEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/puzzle/new" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PuzzleEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/puzzle/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <PuzzleEditor />
          </ProtectedRoute>
        } />
        <Route path="/games/puzzle/:id/play" element={
          <ProtectedRoute>
            <PuzzleGame />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/:gameId/levels" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameLevelEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/lms-integration" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <LMSIntegrationPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ReportDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/departments/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DepartmentDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/departments/create" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DepartmentEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/departments/:id/edit" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <DepartmentEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/moderation" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ContentModerationPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/audit-log" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AuditLogPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/ai-activity" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AiActivityPage />
          </ProtectedRoute>
        } />
        {/* The first name of this page, kept so saved links still work. */}
        <Route path="/admin/claude-activity" element={<Navigate to="/admin/ai-activity" replace />} />
        <Route path="/admin/system-settings" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SystemSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AdminSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <NotificationManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/backup" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <BackupRestorePage />
          </ProtectedRoute>
        } />
        <Route path="/admin/integrations" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <IntegrationSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/roles" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RolePermissionsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/advanced" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditorAdvanced />
          </ProtectedRoute>
        } />
        <Route path="/admin/games/advanced/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <GameEditorAdvanced />
          </ProtectedRoute>
        } />
        <Route path="/admin/system-config" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <SystemConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/user-import" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <UserImportPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/points" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <RewardPointsConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/store" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <StoreAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/marketplace" element={<Navigate to="/admin/store" replace />} />
        <Route path="/admin/super" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <SuperAdminDashboard />
            <BottomHubBar />
          </ProtectedRoute>
        } />
        <Route path="/admin/org-hierarchy" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <OrgHierarchyPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/org-structure" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <OrgHierarchyPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/wellness-analytics" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <WellnessAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/announcements/insights" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <AnnouncementInsightsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <ReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/branding" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin']}>
            <CompanyBrandingPage />
          </ProtectedRoute>
        } />

        {/* Super Admin Routes */}
        <Route path="/super-admin" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <SuperAdminDashboard />
            <BottomHubBar />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/companies" element={
          <ProtectedRoute requiredRoles={['super-super-admin']}>
            <SuperAdminDashboard />
            <BottomHubBar />
          </ProtectedRoute>
        } />

        {/* Onboarding Routes */}
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingFlow />
          </ProtectedRoute>
        } />

        {/* Task Management Routes */}
        <Route path="/tasks" element={
          <ProtectedRoute>
            <MyTasksPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/my-tasks" element={
          <ProtectedRoute>
            <MyTasksPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/kanban" element={<Navigate to="/tasks/manager" replace />} />
        {/* Links to /tasks/<id> (notifications, emails) open the task manager with that task. */}
        <Route path="/tasks/:id" element={<TaskIdRedirect />} />
        <Route path="/tasks/share/:token" element={<PublicBoardPage />} />
        <Route path="/support" element={<ProtectedRoute><SupportPortalPage /></ProtectedRoute>} />
        <Route path="/support/tickets/:id" element={<ProtectedRoute><SupportTicketDetailPage /></ProtectedRoute>} />
        <Route path="/admin/support" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin', 'manager', 'it_support']}>
            <SupportAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/support/tickets/:id" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin', 'manager', 'it_support']}>
            <SupportTicketDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/support/kb" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin', 'manager', 'it_support']}>
            <SupportKbAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/system/errors" element={
          <ProtectedRoute requiredRoles={['admin', 'company-super-admin', 'super-super-admin', 'manager']}>
            <SystemErrorsPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/manager" element={
          <ProtectedRoute>
            <TaskManagerHub />
          </ProtectedRoute>
        } />
        <Route path="/tasks/manager/workstream/:id" element={
          <ProtectedRoute>
            <WorkstreamPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/manager/task/:id" element={
          <ProtectedRoute>
            <KanbanTaskDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/tasks/manager/calendar" element={
          <ProtectedRoute>
            <TasksCalendar />
          </ProtectedRoute>
        } />

        {/* Company AI Chat */}
        <Route path="/ask-ai" element={
          <ProtectedRoute>
            <CompanyAIChatPage />
          </ProtectedRoute>
        } />

        {/* Error Routes */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
